import { compareNumbers } from './numberExtractor';

export interface MatchedPair {
  ixrsId: string;
  vialId: string;
  similarity: number;
}

export interface ReconciliationResult {
  matchedPairs: MatchedPair[];
  unmatchedIxrsIds: string[];
  extraVialIds: string[];
  overallMatch: boolean;
}

/**
 * Reconcile IXRS drug IDs with scanned vial IDs
 * Uses greedy matching to pair IDs with highest similarity first
 *
 * @param ixrsIds - Array of drug IDs from IXRS document
 * @param vialIds - Array of IDs scanned from vials
 * @returns Reconciliation result with matched pairs and unmatched/extra IDs
 */
export function reconcileIds(
  ixrsIds: string[],
  vialIds: string[]
): ReconciliationResult {
  // Deduplicate inputs
  const uniqueIxrsIds = [...new Set(ixrsIds.map(id => id.trim().toUpperCase()))];
  const uniqueVialIds = [...new Set(vialIds.map(id => id.trim().toUpperCase()))];

  // Track which IDs have been matched
  const matchedIxrs = new Set<string>();
  const matchedVials = new Set<string>();

  // Calculate all pairwise similarities
  const allPairs: Array<{
    ixrsId: string;
    vialId: string;
    similarity: number;
  }> = [];

  for (const ixrsId of uniqueIxrsIds) {
    for (const vialId of uniqueVialIds) {
      const { match, similarity } = compareNumbers(ixrsId, vialId);
      // Only consider pairs that meet the match threshold
      if (match || similarity >= 0.9) {
        allPairs.push({ ixrsId, vialId, similarity });
      }
    }
  }

  // Sort by similarity descending (greedy matching - best matches first)
  allPairs.sort((a, b) => b.similarity - a.similarity);

  // Greedily assign matches
  const matchedPairs: MatchedPair[] = [];

  for (const pair of allPairs) {
    // Skip if either ID is already matched
    if (matchedIxrs.has(pair.ixrsId) || matchedVials.has(pair.vialId)) {
      continue;
    }

    // Record the match
    matchedPairs.push({
      ixrsId: pair.ixrsId,
      vialId: pair.vialId,
      similarity: pair.similarity
    });
    matchedIxrs.add(pair.ixrsId);
    matchedVials.add(pair.vialId);
  }

  // Find unmatched IXRS IDs (vials missing)
  const unmatchedIxrsIds = uniqueIxrsIds.filter(id => !matchedIxrs.has(id));

  // Find extra vial IDs (not on IXRS)
  const extraVialIds = uniqueVialIds.filter(id => !matchedVials.has(id));

  // Overall match is only true if all IXRS IDs are matched and no extras
  const overallMatch = unmatchedIxrsIds.length === 0 && extraVialIds.length === 0;

  return {
    matchedPairs,
    unmatchedIxrsIds,
    extraVialIds,
    overallMatch
  };
}

/**
 * Get a summary status for display
 */
export function getReconciliationStatus(result: ReconciliationResult): {
  status: 'match' | 'mismatch';
  summary: string;
} {
  if (result.overallMatch) {
    const count = result.matchedPairs.length;
    return {
      status: 'match',
      summary: `All ${count} vial${count !== 1 ? 's' : ''} verified`
    };
  }

  const issues: string[] = [];

  if (result.unmatchedIxrsIds.length > 0) {
    issues.push(`${result.unmatchedIxrsIds.length} missing vial${result.unmatchedIxrsIds.length !== 1 ? 's' : ''}`);
  }

  if (result.extraVialIds.length > 0) {
    issues.push(`${result.extraVialIds.length} extra vial${result.extraVialIds.length !== 1 ? 's' : ''}`);
  }

  return {
    status: 'mismatch',
    summary: issues.join(', ')
  };
}
